import React from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useOkta } from '@hooks/useOkta'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useIdleTimeout } from '@hooks/useIdleTimeout'
import { IdleTimeoutModal } from '@ui/IdleTimeoutModal'
import { IDLE_TIMEOUT_TOTAL_MINUTES, IDLE_TIMEOUT_WARNING_MINUTES, IDLE_TIMEOUT_COUNTDOWN_SECONDS, OKTA_TOKEN_STORAGE } from '@constants'
import { useAuthentication } from '@features/authentication/hooks'
import { IMkAuthenticationServiceContext, AuthenticationContext } from './_context'
import { MkAuthenticationService } from '../../services/AuthService/_authService'
import type { TMkAuthenticationState } from '../../types'
import authenticationReducer from '../../store/_reducer'

export interface IMkAuthenticationProviderProps {
  children: React.ReactNode
}

// IdleTimeoutWrapper component that can safely use the authentication context
const IdleTimeoutWrapper: React.FC = () => {
  const { isAuthenticated, isAuthorized } = useAuthentication()

  // Memoize idle timeout options to prevent unnecessary re-renders
  const idleTimeoutOptions = React.useMemo(
    () => ({
      idleTimeoutMinutes: IDLE_TIMEOUT_TOTAL_MINUTES,
      warningTimeoutMinutes: IDLE_TIMEOUT_WARNING_MINUTES,
      countdownSeconds: IDLE_TIMEOUT_COUNTDOWN_SECONDS,
    }),
    [],
  )

  const idleTimeout = useIdleTimeout(idleTimeoutOptions)

  // Only show idle timeout for logged in users, exclude test pages
  if (!isAuthenticated || !isAuthorized || window.location.pathname.includes('/test/')) {
    return null
  }

  return (
    <IdleTimeoutModal
      isOpen={idleTimeout.showWarning}
      onStaySignedIn={idleTimeout.handleStaySignedIn}
      onSignOut={idleTimeout.handleSignOut}
      countdownSeconds={idleTimeout.countdownSeconds}
    />
  )
}

export const AuthenticationProvider: React.FC<IMkAuthenticationProviderProps> = ({ children }) => {
  const [state, dispatch] = React.useReducer(
    authenticationReducer,
    undefined as unknown as TMkAuthenticationState,
    () => MkAuthenticationService.initialState() as TMkAuthenticationState,
  )

  const { authBrand } = useSiteContext()

  const { oktaAuthState, oktaAuth } = useOkta()

  const { switchIdleTimeout } = useFlags()

  const [isSharedSessionSyncInProgress, setIsSharedSessionSyncInProgress] = React.useState(false)
  const hasAttemptedSessionSyncRef = React.useRef(false)
  const isSessionSyncInProgressRef = React.useRef(false)
  const isExternalLogoutSyncInProgressRef = React.useRef(false)

  // Memoize dispatch to prevent unnecessary service recreations
  const stableDispatch = React.useCallback(dispatch, [])

  // Use ref to track the current service instance
  const serviceRef = React.useRef<MkAuthenticationService | null>(null)
  const lastStateRef = React.useRef({
    authBrand,
    isAuthenticated: state.isAuthenticated,
    isAuthorized: state.isAuthorized,
    hasOktaAuth: !!oktaAuth,
  })

  // Cleanup MFA interval on provider unmount to prevent memory leaks
  React.useEffect(
    () => () => {
      // Clean up the static MFA cleanup interval when the provider unmounts
      MkAuthenticationService.cleanupRecentlyTriggeredMfaInterval()
    },
    [],
  )

  // Only create new service when core dependencies actually change
  const authService = React.useMemo(() => {
    const currentState = {
      authBrand,
      isAuthenticated: state.isAuthenticated,
      isAuthorized: state.isAuthorized,
      hasOktaAuth: !!oktaAuth,
    }

    // Check if we actually need to create a new service
    const needsNewService =
      !serviceRef.current ||
      lastStateRef.current.authBrand !== currentState.authBrand ||
      lastStateRef.current.isAuthenticated !== currentState.isAuthenticated ||
      lastStateRef.current.isAuthorized !== currentState.isAuthorized ||
      lastStateRef.current.hasOktaAuth !== currentState.hasOktaAuth

    if (needsNewService) {
      // Creating new AuthenticationService instance

      const newService = new MkAuthenticationService({ ...state, authBrand }, stableDispatch, { oktaAuth, oktaAuthState })
      serviceRef.current = newService
      lastStateRef.current = currentState
      return newService
    }

    // Update the existing service with new state if needed
    if (serviceRef.current) {
      serviceRef.current.updateState({ ...state, authBrand })
    }

    return serviceRef.current!
  }, [
    authBrand,
    state.isAuthenticated,
    state.isAuthorized,
    state.session,
    state.error,
    state.isAccountLocked,
    state.lockoutEndTime,
    state.lockedUsername,
    state.isLoggingOut,
    oktaAuth,
    oktaAuthState,
    stableDispatch,
  ])

  // Compute isAuthInitialized from Okta state (provider-agnostic abstraction)
  // When swapping auth providers, only this computation needs to change
  const isAuthInitialized = React.useMemo(() => (oktaAuthState ? !oktaAuthState.isPending : false), [oktaAuthState])

  const shouldSkipCrossTabSync = React.useCallback((): boolean => {
    const path = window.location.pathname
    return path.includes('/account/login/callback') || path.includes('/account/logout/callback')
  }, [])

  const syncSessionFromOktaTokens = React.useCallback(() => {
    if (shouldSkipCrossTabSync() || state.isAuthorized || state.isLoggingOut || isSessionSyncInProgressRef.current) {
      return
    }

    hasAttemptedSessionSyncRef.current = true
    isSessionSyncInProgressRef.current = true
    setIsSharedSessionSyncInProgress(true)

    authService.syncAuthorizedSessionFromOktaTokens().finally(() => {
      isSessionSyncInProgressRef.current = false
      setIsSharedSessionSyncInProgress(false)
    })
  }, [authService, shouldSkipCrossTabSync, state.isAuthorized, state.isLoggingOut])

  const syncExternalLogout = React.useCallback(() => {
    if (shouldSkipCrossTabSync() || state.isLoggingOut || !state.isAuthorized || isExternalLogoutSyncInProgressRef.current) {
      return
    }

    isExternalLogoutSyncInProgressRef.current = true
    authService.clearLocalSessionAfterExternalLogout().finally(() => {
      isExternalLogoutSyncInProgressRef.current = false
    })
  }, [authService, shouldSkipCrossTabSync, state.isAuthorized, state.isLoggingOut])

  // If Okta tokens already exist (new tab / token sync), rebuild local authorized session once.
  React.useEffect(() => {
    if (!isAuthInitialized || !oktaAuthState?.isAuthenticated) {
      hasAttemptedSessionSyncRef.current = false
      isSessionSyncInProgressRef.current = false
      setIsSharedSessionSyncInProgress(false)
      return
    }

    if (!state.isAuthorized && !hasAttemptedSessionSyncRef.current) {
      syncSessionFromOktaTokens()
    }
  }, [isAuthInitialized, oktaAuthState?.isAuthenticated, state.isAuthorized, syncSessionFromOktaTokens])

  // If Okta reports unauthenticated while this tab is authorized, clear local session.
  React.useEffect(() => {
    if (!isAuthInitialized || oktaAuthState?.isAuthenticated !== false) {
      return
    }

    syncExternalLogout()
  }, [isAuthInitialized, oktaAuthState?.isAuthenticated, syncExternalLogout])

  // Cross-tab fallback: storage updates trigger sync; AuthService decides token truth.
  React.useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return
      const isRelevantTokenStorageEvent = event.key === null || event.key === OKTA_TOKEN_STORAGE
      if (!isRelevantTokenStorageEvent) return
      if (!isAuthInitialized || state.isLoggingOut || shouldSkipCrossTabSync()) return

      if (event.newValue === null) {
        syncExternalLogout()
        return
      }

      if (!state.isAuthorized) {
        hasAttemptedSessionSyncRef.current = false
        syncSessionFromOktaTokens()
        return
      }

      syncExternalLogout()
    }

    window.addEventListener('storage', onStorageChange)
    return () => {
      window.removeEventListener('storage', onStorageChange)
    }
  }, [isAuthInitialized, shouldSkipCrossTabSync, state.isAuthorized, state.isLoggingOut, syncExternalLogout, syncSessionFromOktaTokens])

  const isSharedSessionBootstrapPending = React.useMemo(() => {
    if (!isAuthInitialized || !oktaAuthState?.isAuthenticated) return false
    if (state.isAuthorized || state.isLoggingOut || shouldSkipCrossTabSync()) return false

    return isSharedSessionSyncInProgress || !hasAttemptedSessionSyncRef.current
  }, [isAuthInitialized, oktaAuthState?.isAuthenticated, state.isAuthorized, state.isLoggingOut, shouldSkipCrossTabSync, isSharedSessionSyncInProgress])

  // Memoize the context props to prevent unnecessary recalculations
  // Must depend on state.error and lockout properties directly since authService instance doesn't change when these change
  const contextProps = React.useMemo(
    () => authService.getContextProps<IMkAuthenticationServiceContext>(),
    [authService, state.session, state.error, state.isAccountLocked, state.lockoutEndTime, state.lockedUsername, state.isLoggingOut],
  )

  // Memoize the context value to prevent unnecessary re-renders
  // Merge isAuthInitialized from provider computation into context value
  const contextValue = React.useMemo<IMkAuthenticationServiceContext>(
    () => ({
      ...contextProps,
      isAuthInitialized,
      isSharedSessionSyncInProgress: isSharedSessionBootstrapPending,
      authService,
      switchMembership: contextProps.switchMembership,
    }),
    [contextProps, isAuthInitialized, isSharedSessionBootstrapPending, authService],
  )

  return (
    <AuthenticationContext.Provider value={contextValue}>
      {children}
      {switchIdleTimeout && <IdleTimeoutWrapper />}
    </AuthenticationContext.Provider>
  )
}
